import { useEffect, useImperativeHandle, useState } from "react";

import { Table, Transfer } from "antd";
import { t } from "i18next";
import PropTypes from "prop-types";

import useSystemsByPropertyId from "../../../../../api/hooks/useSystemsByPropertyId";
import useWindowSize from "../../../../../hooks/useWindowSize";
import {
  columns,
  dataStructure,
  filterLeftSource,
  filterRightSource,
  flattenTree,
  renderTitle,
} from "../helperFuncs/helperFunctions";
import "../scss/ZoneSystems.scss";
import NameFilterGroup from "./NameFilterGroup";

function ZoneSystems({
  editFloorFormReference,
  propertyId,
  setDisableSaveChanges,
  setIsFormDirty,
  floorSystemsRef,
  zoneSerialNumber,
}) {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);
  const [leftTableData, setLeftTableData] = useState([]);
  const [rightTableData, setRightTableData] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const window = useWindowSize();

  const { data: systemsWithDevices } = useSystemsByPropertyId(
    propertyId,
    zoneSerialNumber ?? null,
    {},
    { enabled: true },
  );

  useImperativeHandle(floorSystemsRef, () => ({
    setDataSource,
    setExpandedRowKeys,
    setTargetKeys,
    setLeftTableData,
    setRightTableData,
  }));

  useEffect(() => {
    if (!systemsWithDevices) return;

    const filteredSystems = searchQuery
      ? {
          AssignedSystems: systemsWithDevices.AssignedSystems.map((system) => {
            const { Name, UniqueId, Devices } = system;
            const query = searchQuery.toLowerCase();

            // Filter devices assigned to current zone only
            const filteredDevices = Devices.filter(
              (device) => device.ZoneSerialNumber === zoneSerialNumber,
            );

            // Check if system or any filtered device matches the search query
            const searchMatches =
              Name?.toLowerCase().includes(query) ||
              UniqueId?.toLowerCase().includes(query) ||
              filteredDevices.some((device) =>
                device.SerialNumber?.toLowerCase().includes(query),
              );

            // Include system only if search matches AND has assigned devices
            if (searchMatches && filteredDevices.length > 0) {
              return {
                ...system,
                Devices: filteredDevices,
              };
            }

            return null; // exclude if no assigned devices or no search match
          }).filter(Boolean),
          UnassignedSystems: systemsWithDevices.UnassignedSystems.map(
            (system) => {
              const { Name, UniqueId, Devices } = system;
              const query = searchQuery.toLowerCase();

              // Filter devices assigned elsewhere (unassigned or different zone)
              const filteredDevices = Devices.filter(
                (device) => device.ZoneSerialNumber !== zoneSerialNumber,
              );

              const searchMatches =
                Name?.toLowerCase().includes(query) ||
                UniqueId?.toLowerCase().includes(query) ||
                filteredDevices.some((device) =>
                  device.SerialNumber?.toLowerCase().includes(query),
                );

              // Include system only if search matches and has filtered devices
              if (searchMatches && filteredDevices.length > 0) {
                return {
                  ...system,
                  Devices: filteredDevices,
                };
              }
              return null; // exclude otherwise
            },
          ).filter(Boolean),
        }
      : {
          AssignedSystems: systemsWithDevices.AssignedSystems.map((system) => {
            const { Devices } = system;

            // Filter devices assigned to current zone only
            const filteredDevices = Devices.filter(
              (device) => device.ZoneSerialNumber === zoneSerialNumber,
            );

            // Include system only if has assigned devices
            if (filteredDevices.length > 0) {
              return {
                ...system,
                Devices: filteredDevices,
              };
            }

            return null; // exclude if no assigned devices or no search match
          }).filter(Boolean),
          UnassignedSystems: [
            ...systemsWithDevices.UnassignedSystems,
            ...systemsWithDevices.AssignedSystems.map((system) => {
              // Filter devices that are not assigned to current zone
              const filteredDevices = system.Devices.filter(
                (device) => device.ZoneSerialNumber !== zoneSerialNumber,
              );

              if (filteredDevices.length > 0) {
                // Return new system with only filtered devices
                return {
                  ...system,
                  Devices: filteredDevices,
                };
              }
              // Otherwise return null (no unassigned devices)
              return null;
            }).filter(Boolean),
          ],
        };

    const allSystems = dataStructure(filteredSystems);
    const allRowKeys = allSystems.map((system) => system.key);

    const assignedSystems = allSystems.filter((x) => x.assigned);
    const unassignedSystems = allSystems.filter((x) => !x.assigned);

    const flattenTargetKeys = flattenTree(
      zoneSerialNumber ? unassignedSystems : allSystems,
    ).map((x) => x.key);

    setExpandedRowKeys(allRowKeys);
    setDataSource(allSystems);
    setTargetKeys(flattenTargetKeys);
    setLeftTableData(zoneSerialNumber ? assignedSystems : []);
    setRightTableData(unassignedSystems);
  }, [systemsWithDevices, searchQuery, zoneSerialNumber]);

  const handleChange = (newTargetKeys, direction, moveKeys) => {
    let updatedTargetKeys = [...newTargetKeys];

    if (direction === "right") {
      moveKeys.forEach((key) => {
        const parent = dataSource.find((p) =>
          p.children.some((child) => child.key === key),
        );
        if (parent && !updatedTargetKeys.includes(parent.key)) {
          updatedTargetKeys.push(parent.key);
        }
      });
    } else {
      moveKeys.forEach((key) => {
        const parent = dataSource.find(
          (p) => p.key === key || p.children.some((c) => c.key === key),
        );
        if (
          parent &&
          parent.children.every(
            (child) => !updatedTargetKeys.includes(child.key),
          )
        ) {
          updatedTargetKeys = updatedTargetKeys.filter(
            (targetKey) => targetKey !== parent.key,
          );
        }
      });
    }

    const newLeftData = filterLeftSource(dataSource, updatedTargetKeys);
    const newRightData = filterRightSource(dataSource, updatedTargetKeys);

    setTargetKeys(updatedTargetKeys);
    setSelectedKeys([]);
    setLeftTableData(newLeftData);
    setRightTableData(newRightData);
    setDisableSaveChanges(false);
    setIsFormDirty(true);

    editFloorFormReference?.setFieldsValue?.({
      Systems: {
        AssignedSystems: newLeftData,
        UnassignedSystems: newRightData,
      },
    });
  };

  const handleRowSelection = (selectedRowKeys) => {
    const updatedKeys = [...selectedRowKeys];

    // Remove parent keys if children are selected and already assigned
    updatedKeys.forEach((key) => {
      const parent = dataSource.find((p) =>
        p.children.some((c) => c.key === key),
      );
      if (parent && targetKeys.includes(parent.key)) {
        const index = updatedKeys.indexOf(parent.key);
        if (index !== -1) updatedKeys.splice(index, 1);
      }
    });

    dataSource.forEach((parent) => {
      const childKeys = parent.children.map((child) => child.key);
      const anyChildSelected = childKeys.some((k) => updatedKeys.includes(k));

      if (!anyChildSelected) {
        const index = updatedKeys.indexOf(parent.key);
        if (index !== -1) updatedKeys.splice(index, 1);
      }
    });

    setSelectedKeys(updatedKeys);
  };

  return (
    <div className="floor-system-table-transfer">
      <NameFilterGroup
        handleSearchChange={(e) => setSearchQuery(e.target.value)}
      />
      <Transfer
        className={window.width <= 1024 ? "flex-col" : "flex-row"}
        dataSource={dataSource}
        titles={[
          renderTitle(leftTableData, t("create_zone.assigned_devices")),
          renderTitle(rightTableData, t("create_zone.unassigned_devices")),
        ]}
        targetKeys={targetKeys}
        selectedKeys={selectedKeys}
        onChange={handleChange}
        showSelectAll={false}
        operations={[t("create_zone.unassign"), t("create_zone.add_to_zone")]}
      >
        {({ direction, selectedKeys }) =>
          dataSource.length > 0 && (
            <Table
              style={{ height: "55vh", overflowY: "auto" }}
              rowKey="key"
              columns={columns}
              dataSource={direction === "left" ? leftTableData : rightTableData}
              pagination={false}
              size="small"
              rowSelection={{
                checkStrictly: false,
                selectedRowKeys: selectedKeys,
                onChange: handleRowSelection,
              }}
              expandable={{
                defaultExpandAllRows: true,
                expandedRowKeys,
                expandIcon: () => null,
                rowExpandable: (record) => record.children?.length > 0,
                indentSize: 0,
              }}
            />
          )
        }
      </Transfer>
    </div>
  );
}

ZoneSystems.propTypes = {
  editFloorFormReference: PropTypes.object,
  propertyId: PropTypes.number.isRequired,
  setDisableSaveChanges: PropTypes.func,
  setIsFormDirty: PropTypes.func,
  floorSystemsRef: PropTypes.object,
  zoneSerialNumber: PropTypes.any,
};

ZoneSystems.defaultProps = {
  editFloorFormReference: {},
  setDisableSaveChanges: () => {},
  setIsFormDirty: () => {},
  floorSystemsRef: {},
  zoneSerialNumber: null,
};

export default ZoneSystems;
